import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash2, DollarSign, Calendar, Tag, FileText } from 'lucide-react';
import { toast } from 'sonner';
import Swal from 'sweetalert2';

import API from '../api_config';
import { useAuth } from '../contexts/AuthContext';

function formatCLP(amount) {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
}

export default function ExpensesPage() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: 'otros',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const fetchExpenses = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/expenses`);
      setExpenses(response.data);
    } catch (error) {
      toast.error('Error al cargar los gastos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        notes: formData.notes || null
      };
      await axios.post(`${API}/expenses`, payload);
      toast.success('Gasto registrado exitosamente');
      setIsFormOpen(false);
      setFormData({
        description: '',
        amount: '',
        category: 'otros',
        date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar el gasto');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: "No podrás deshacer esta acción",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API}/expenses/${id}`);
        Swal.fire('¡Eliminado!', 'El registro de gasto ha sido eliminado.', 'success');
        fetchExpenses();
      } catch (error) {
        Swal.fire('Error', 'No se pudo eliminar el gasto.', 'error');
      }
    }
  };

  const categoriesMap = {
    arriendo: 'Arriendo',
    servicios: 'Servicios Básicos',
    sueldos: 'Sueldos y Personal',
    impuestos: 'Impuestos y Tasas',
    mantenimiento: 'Mantenimiento',
    otros: 'Otros Gastos'
  };

  const totalExpensesSum = expenses.reduce((sum, exp) => sum + exp.amount, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Gastos Operacionales</h1>
          <p className="text-muted-foreground">Registra y controla los egresos de tu PYME</p>
        </div>
        <div>
          <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full shadow-lg">
                <Plus className="h-5 w-5 mr-2" />
                Registrar Gasto
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Registrar Egresos / Gasto</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="description">Descripción / Glosa</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Ej. Pago luz de local"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Monto ($ CLP)</Label>
                    <Input
                      id="amount"
                      type="number"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="Monto"
                      min="1"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date">Fecha</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Categoría</Label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {Object.entries(categoriesMap).map(([key, val]) => (
                      <option key={key} value={key}>{val}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notas adicionales</Label>
                  <Input
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Opcional..."
                  />
                </div>

                <Button type="submit" className="w-full">
                  Registrar Egresos
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Resumen de Gastos */}
      <Card className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 border-red-200 dark:border-red-900">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Gastos Registrados</p>
              <h2 className="text-4xl font-bold font-mono text-red-600 dark:text-red-400">
                {formatCLP(totalExpensesSum)}
              </h2>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground mb-1">Egresos Realizados</p>
              <p className="text-3xl font-bold">{expenses.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Listado de Gastos */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Cargando...</div>
      ) : expenses.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12 text-muted-foreground">
            <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50 text-red-500" />
            <p>No hay gastos operacionales registrados en este período.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {expenses.map((expense) => (
            <Card key={expense.id} className="hover:shadow-md transition-shadow relative overflow-hidden">
              <CardHeader className="pb-2 flex flex-row justify-between items-start">
                <div className="space-y-1">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                    <Tag className="h-3.5 w-3.5" />
                    {categoriesMap[expense.category] || expense.category}
                  </div>
                  <h3 className="font-semibold text-lg pt-1">{expense.description}</h3>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-muted-foreground hover:text-red-500"
                  onClick={() => handleDelete(expense.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-2xl font-mono font-bold text-red-700 dark:text-red-400">
                  {formatCLP(expense.amount)}
                </div>
                <div className="flex flex-col gap-1.5 border-t pt-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>Fecha: {new Date(expense.date + 'T00:00:00').toLocaleDateString('es-CL')}</span>
                  </div>
                  {expense.notes && (
                    <div className="flex items-start gap-1">
                      <FileText className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span className="italic truncate">{expense.notes}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
